import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Cabine, Segmentos } from "../componentes/FerramentaUi";
import { HonorariosEditor, PartesEditor } from "../componentes/PeritoEditores";
import { BOTAO_SECUNDARIO, Campo, IconeDocumento, IconeEnviar, IconeMais, IconeTexto } from "../componentes/PeritoUi";
import { CAMPO } from "../componentes/ui";
import { recusaDaLeitura, tipoDoArquivo } from "../lib/perito/arquivos";
import { aplicarMascaraCnj, parseCnj } from "../lib/perito/cnj";
import { cpfCnpjValido, mascaraCpfCnpj } from "../lib/perito/cpfCnpj";
import { lerDespacho } from "../lib/perito/dados";
import { hoje, maisDias } from "../lib/perito/datas";
import { LIMITE_OBSERVACOES, ehSoNumeroCnj, emLista, leituraVazia, montarCadastro, type CadastroInicial } from "../lib/perito/despacho";
import { PRIORIDADE_LABEL } from "../lib/perito/format";
import { TEMPLATES_TAREFA, buscarModelo, modelosDaEtapa } from "../lib/perito/modelos";
import { ETAPAS, ETAPA_LABEL, type EtapaNum } from "../lib/perito/pipeline";
import { usePerito } from "../lib/perito/store";
import type { Honorario, Parte, Prioridade } from "../lib/perito/tipos";
import { TIPOS_PERICIA, TRIBUNAIS, buscarTribunalPorCodigoCnj } from "../lib/perito/tribunais";

// O cadastro de uma perícia — portado de `src/routes/_authenticated/pericias.nova.tsx` (a entrada pelo
// despacho) e de `src/components/pericias/PericiaWizard.tsx` (os seis passos) da Central do Perito, no commit
// f5c1923. A fase A (000385) trouxe os seis passos; a fase C (000386) trouxe a entrada: o aluno envia o
// despacho ou cola o texto, a função `ler-despacho` o lê, e o que ela achou PREENCHE os seis passos.
//
// O que muda de lá, de propósito:
//   * um caminho só de cadastro: lá a leitura abria uma segunda tela de conferência, com os mesmos campos dos
//     seis passos escritos de novo; aqui ela preenche os seis passos, e uma caixa no alto diz o que achou e o
//     que faltou — a conferência de lá pintava os campos vazios para dizer isso;
//   * o prazo sugerido de cada tarefa conta do dia de HOJE em São Paulo (lá, `Date.now()` em UTC dava o dia
//     seguinte das 21h à meia-noite);
//   * o status não é escolhido: ele acompanha a etapa (`statusDaEtapa`), e o kanban de lá não finalizava;
//   * o CPF ou CNPJ incompleto IMPEDE salvar, em vez de só avisar: o banco recusa, e a gravação recusada na
//     fila desfaz a perícia inteira na tela do aluno. Pela mesma razão, as observações acima do teto do banco.

const RAIZ = "/ferramenta/central-do-perito";
const PASSOS = ["Processo", "Partes", "Perícia", "Financeiro", "Tarefas", "Observações"] as const;

type Modo = "entrada" | "lendo" | "cadastro";
/** O despacho enviado: fica em memória até a perícia ser salva, e então vai aos documentos dela. */
interface Anexo { arquivo: File; nome: string; tipo: string }
/** O que a caixa do alto do cadastro diz sobre a leitura. */
interface ResultadoDaLeitura { achou: string[]; faltou: string[]; recusa?: string; podeTentarDeNovo?: boolean; soNumero?: boolean }

/** O arquivo em base64, sem o prefixo `data:…;base64,`. */
function comoBase64(arquivo: File): Promise<string> {
  return new Promise((resolver, rejeitar) => {
    const leitor = new FileReader();
    leitor.onload = () => resolver(String(leitor.result).split(",")[1] ?? "");
    leitor.onerror = () => rejeitar(leitor.error);
    leitor.readAsDataURL(arquivo);
  });
}

/** Uma tarefa escolhida no cadastro, antes de a perícia existir: de um modelo, ou escrita pelo aluno. */
interface TarefaInicial {
  chave: string;
  modeloId?: string;
  titulo?: string;
  prazo: string;
  prazoSugerido: boolean;
}

function tarefasDaEtapa(etapa: EtapaNum): TarefaInicial[] {
  return modelosDaEtapa(etapa).map((m) => ({ chave: m.id, modeloId: m.id, prazo: maisDias(hoje(), m.prazoDias), prazoSugerido: true }));
}

/** A tarefa de aceite usa o prazo para aceitar o encargo, quando ele existe (lá: `usaPrazoAceitacao`). */
function comPrazoDeAceitacao(lista: TarefaInicial[], data: string): TarefaInicial[] {
  if (!data) return lista;
  return lista.map((t) => (t.modeloId && buscarModelo(t.modeloId)?.usaPrazoAceitacao ? { ...t, prazo: data, prazoSugerido: false } : t));
}

export default function PeritoNova() {
  const navegar = useNavigate();
  const s = usePerito();
  const [passo, setPasso] = useState(0);
  const [numero, setNumero] = useState("");
  const [apelido, setApelido] = useState("");
  const [tribunal, setTribunal] = useState("");
  const [vara, setVara] = useState("");
  const [fontePagadora, setFontePagadora] = useState("");
  const [cpfCnpjFonte, setCpfCnpjFonte] = useState("");
  const [tipo, setTipo] = useState("");
  const [etapa, setEtapa] = useState<EtapaNum>(1);
  const [proximaAcao, setProximaAcao] = useState("");
  const [prioridade, setPrioridade] = useState<Prioridade>("media");
  const [dataNomeacao, setDataNomeacao] = useState("");
  const [dataEntrega, setDataEntrega] = useState("");
  const [observacoes, setObservacoes] = useState("");
  const [partes, setPartes] = useState<Parte[]>([]);
  const [honorarios, setHonorarios] = useState<Honorario[]>([]);
  const [tarefas, setTarefas] = useState<TarefaInicial[]>(() => tarefasDaEtapa(1));
  const [novoTitulo, setNovoTitulo] = useState("");
  const [novoPrazo, setNovoPrazo] = useState("");
  const [prazoAceitacao, setPrazoAceitacao] = useState("");
  const [erro, setErro] = useState<string | null>(null);

  // A entrada pelo despacho (fase C).
  const [modo, setModo] = useState<Modo>("entrada");
  const [texto, setTexto] = useState("");
  const [anexo, setAnexo] = useState<Anexo | null>(null);
  const [arrastando, setArrastando] = useState(false);
  const [erroEntrada, setErroEntrada] = useState<string | null>(null);
  const [leitura, setLeitura] = useState<ResultadoDaLeitura | null>(null);
  const escolherArquivo = useRef<HTMLInputElement>(null);
  const leituraEmCurso = useRef<AbortController | null>(null);
  useEffect(() => () => leituraEmCurso.current?.abort(), []);

  const tribunalObj = TRIBUNAIS.find((t) => t.sigla === tribunal);
  const cnj = parseCnj(numero);
  const escolhidas = new Set(tarefas.map((t) => t.modeloId).filter((m): m is string => !!m));

  const trocarEtapa = (e: EtapaNum) => {
    // A etapa decide as tarefas sugeridas: trocar a etapa recarrega a lista, como lá. As tarefas próprias
    // que o aluno escreveu ficam.
    setEtapa(e);
    setTarefas((lista) => [...comPrazoDeAceitacao(tarefasDaEtapa(e), prazoAceitacao), ...lista.filter((t) => !t.modeloId)]);
  };
  const mudarPrazoAceitacao = (v: string) => {
    setPrazoAceitacao(v);
    setTarefas((l) => comPrazoDeAceitacao(l, v));
  };

  /** O que a leitura (ou só o número) achou vira o cadastro, do primeiro passo. */
  const aplicar = (c: CadastroInicial) => {
    setNumero(c.numero);
    setApelido(c.apelido);
    setTribunal(c.tribunal);
    setVara(c.vara);
    setTipo(c.tipo);
    setDataNomeacao(c.dataNomeacao);
    setDataEntrega(c.dataEntrega);
    setPrazoAceitacao(c.prazoAceitacao);
    setProximaAcao(c.proximaAcao);
    setObservacoes(c.observacoes);
    setPartes(c.partes);
    setEtapa(1);
    setTarefas(comPrazoDeAceitacao(tarefasDaEtapa(1), c.prazoAceitacao));
    setPasso(0);
    setErro(null);
  };

  const ler = async (arquivo?: File) => {
    setErroEntrada(null);
    const conteudo = texto.trim();
    if (!arquivo && !conteudo) {
      setErroEntrada("Cole o texto do despacho, ou envie o arquivo.");
      return;
    }
    // Só o número do processo: o próprio CNJ diz o tribunal, sem gastar a leitura (como lá).
    if (!arquivo && ehSoNumeroCnj(conteudo)) {
      const c = montarCadastro(null, conteudo);
      aplicar(c);
      setAnexo(null);
      setLeitura({ achou: c.achou, faltou: c.faltou, soNumero: true });
      setModo("cadastro");
      return;
    }
    let enviado: Anexo | null = null;
    let base64 = "";
    if (arquivo) {
      const recusa = recusaDaLeitura(arquivo.name, arquivo.type, arquivo.size);
      const tipo = tipoDoArquivo(arquivo.name, arquivo.type);
      if (recusa || !tipo) {
        setErroEntrada(recusa ?? "Este arquivo não pode ser lido.");
        return;
      }
      try {
        base64 = await comoBase64(arquivo);
      } catch {
        setErroEntrada("Não foi possível abrir o arquivo neste navegador. Tente de novo, ou cole o texto do despacho.");
        return;
      }
      enviado = { arquivo, nome: arquivo.name, tipo };
    }

    leituraEmCurso.current?.abort();
    const controle = new AbortController();
    leituraEmCurso.current = controle;
    setAnexo(enviado);
    setModo("lendo");
    const r = await lerDespacho({ texto: conteudo || undefined, arquivo: enviado ? { nome: enviado.nome, tipo: enviado.tipo, base64 } : undefined }, controle.signal);
    // Cancelada enquanto esperava: o resultado não é usado.
    if (controle.signal.aborted) return;
    leituraEmCurso.current = null;
    if (r.ok) {
      const c = montarCadastro(r.dados, conteudo);
      aplicar(c);
      setLeitura(leituraVazia(c)
        ? { achou: [], faltou: c.faltou, recusa: "A leitura não achou os dados do processo neste documento. Preencha os campos nos passos." }
        : { achou: c.achou, faltou: c.faltou });
    } else {
      // A leitura não deu certo: o que o texto colado tiver de número do processo ainda serve.
      const c = montarCadastro(null, conteudo);
      aplicar(c);
      setLeitura({ achou: c.achou, faltou: c.faltou, recusa: r.detalhe, podeTentarDeNovo: ["limite", "indisponivel", "falha"].includes(r.motivo) });
    }
    setModo("cadastro");
  };

  const cancelarLeitura = () => {
    leituraEmCurso.current?.abort();
    leituraEmCurso.current = null;
    setAnexo(null);
    setModo("entrada");
  };
  const aoDigitarNumero = (v: string) => {
    const mascarado = aplicarMascaraCnj(v);
    setNumero(mascarado);
    const p = parseCnj(mascarado);
    if (p?.valido && !tribunal) {
      const det = buscarTribunalPorCodigoCnj(p.segmento, p.tribunal);
      if (det) setTribunal(det.sigla);
    }
  };
  const criarTarefaPropria = () => {
    const titulo = novoTitulo.trim();
    if (!titulo) return;
    setTarefas((l) => [...l, { chave: crypto.randomUUID(), titulo, prazo: novoPrazo, prazoSugerido: false }]);
    setNovoTitulo("");
    setNovoPrazo("");
  };

  /** O que falta para salvar, dito com o passo em que se resolve. */
  const falta = (): { texto: string; passo: number } | null => {
    if (!numero.trim()) return { texto: "Informe o número do processo.", passo: 0 };
    if (!tribunal) return { texto: "Escolha o tribunal.", passo: 0 };
    if (cpfCnpjFonte && !cpfCnpjValido(cpfCnpjFonte)) return { texto: "O CPF ou CNPJ da fonte pagadora está incompleto. Complete ou apague o campo.", passo: 0 };
    if (!tipo) return { texto: "Escolha o tipo de perícia.", passo: 2 };
    if (observacoes.trim().length > LIMITE_OBSERVACOES) return { texto: `As observações passam de ${LIMITE_OBSERVACOES.toLocaleString("pt-BR")} caracteres. Encurte o texto.`, passo: 5 };
    return null;
  };

  const salvar = () => {
    const f = falta();
    if (f) {
      setErro(f.texto);
      setPasso(f.passo);
      return;
    }
    // As tarefas sugeridas que o aluno tirou não voltam sozinhas quando a etapa for aplicada de novo.
    const removidos = modelosDaEtapa(etapa).map((m) => m.id).filter((id) => !escolhidas.has(id));
    const p = s.addPericia(
      {
        numeroProcesso: numero,
        apelido: apelido.trim() || undefined,
        tribunal,
        vara,
        uf: tribunalObj?.uf,
        tipoPericia: tipo,
        etapa_atual: etapa,
        proximaAcao: proximaAcao.trim() || undefined,
        prioridade,
        partes: partes.filter((x) => x.nome.trim()),
        dataNomeacao: dataNomeacao || undefined,
        dataEntregaLaudo: dataEntrega || undefined,
        honorarios,
        fontePagadora: fontePagadora.trim() || undefined,
        cpfCnpjFonte: cpfCnpjFonte || undefined,
        observacoes: observacoes.trim() || undefined,
        modelosRemovidos: removidos.length ? removidos : undefined,
      },
      { semTarefasAutomaticas: true },
    );
    for (const t of tarefas) {
      if (t.modeloId) s.criarTarefaDeModelo(t.modeloId, p.id, { prazo: t.prazo || undefined, prazoSugerido: t.prazoSugerido && !!t.prazo });
      else if (t.titulo) s.addTarefa({ pericia_id: p.id, titulo: t.titulo, prazo: t.prazo || undefined, prioridade: "media" });
    }
    if (prazoAceitacao) {
      s.addPrazo({ pericia_id: p.id, titulo: "Aceitar o encargo", data: prazoAceitacao, observacao: leitura && !leitura.recusa ? "Prazo identificado no despacho de nomeação" : undefined });
    }
    // O despacho lido fica nos documentos da perícia. A tela não espera o envio: a perícia já está salva, e o
    // documento aparece na aba Documentos quando subir. Se não subir, o aviso diz onde anexar.
    if (anexo) {
      s.anexarDocumento(p.id, anexo.arquivo, anexo.nome, anexo.tipo).catch((e) => {
        console.error("[perito] o despacho não foi guardado:", e);
        usePerito.setState({ aviso: "A perícia foi salva, mas o despacho não foi guardado. Anexe o arquivo na aba Documentos da perícia." });
      });
    }
    navegar(`${RAIZ}/pericias/${p.id}`);
  };

  if (!s.podeEscrever) {
    return <div className="surface p-6 text-sm text-cinza">O cadastro de perícias pede o curso liberado.</div>;
  }
  /** O que o número do processo preencheu, sem ele mesmo ("o número do processo preencheu o tribunal"). */
  const doNumero = (leitura?.achou ?? []).filter((x) => x !== "o número do processo");

  if (modo === "entrada") {
    return (
      <div className="flex flex-col gap-5" data-nova-entrada>
        <Cabine trilha={{ para: `${RAIZ}/pericias`, rotulo: "Perícias" }} rotulo="Nova perícia" titulo="Comece pelo despacho de nomeação"
          descricao="Envie o arquivo ou cole o texto: a leitura automática preenche o processo, o tribunal, a vara, as partes e os prazos, e você confere antes de salvar." />

        {erroEntrada && <p role="alert" className="rounded-xl bg-erro-fundo p-3 text-sm text-erro-tinta" data-erro-entrada>{erroEntrada}</p>}

        <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
          {/* Um <button> de verdade, e não um cartão com onClick: a área de soltar o arquivo também abre pelo teclado. */}
          <button type="button" data-enviar-despacho
            onDragOver={(e) => { e.preventDefault(); setArrastando(true); }}
            onDragLeave={() => setArrastando(false)}
            onDrop={(e) => {
              e.preventDefault();
              setArrastando(false);
              const f = e.dataTransfer.files?.[0];
              if (f) void ler(f);
            }}
            onClick={() => escolherArquivo.current?.click()}
            className={`surface flex min-h-64 flex-col items-center justify-center gap-3 border-2 border-dashed p-7 text-center transition ${arrastando ? "border-laranja bg-alerta-fundo" : "border-borda hover:border-acento"}`}>
            <span className="grid size-14 place-items-center rounded-2xl bg-tile text-laranja-texto"><IconeEnviar className="size-7" /></span>
            <span className="font-display text-lg font-bold text-titulo">Enviar arquivo</span>
            <span className="max-w-sm text-sm leading-relaxed text-cinza">
              PDF ou foto do despacho, até 8 MB. Arraste para cá ou clique. Ao salvar a perícia, o arquivo fica nos documentos dela.
            </span>
          </button>
          <input ref={escolherArquivo} type="file" accept="application/pdf,image/*,.heic,.heif" className="hidden" data-arquivo-despacho
            onChange={(e) => {
              const f = e.target.files?.[0];
              e.target.value = "";
              if (f) void ler(f);
            }} />

          <div className="surface flex min-h-64 flex-col gap-4 p-6">
            <div className="flex items-center gap-3">
              <span className="grid size-11 shrink-0 place-items-center rounded-xl bg-tile text-link"><IconeTexto className="size-5" /></span>
              <div>
                <h2 className="font-display text-lg font-bold text-titulo">Colar texto</h2>
                <p className="text-sm text-cinza">O despacho, o e-mail da intimação ou só o número do processo.</p>
              </div>
            </div>
            <textarea className={`${CAMPO} min-h-32 flex-1 py-3`} value={texto} onChange={(e) => setTexto(e.target.value)}
              placeholder="Cole aqui o texto ou o número do processo…" aria-label="Texto do despacho" data-texto-despacho />
            <button type="button" className="pill-cta min-h-10 self-end px-5 text-sm" onClick={() => void ler()} data-ler-texto>Ler o texto</button>
          </div>
        </div>

        <p className="text-center text-sm text-cinza">
          Prefere digitar?{" "}
          <button type="button" className="font-semibold text-link underline underline-offset-4 hover:no-underline" data-preencher-manualmente
            onClick={() => { setLeitura(null); setAnexo(null); setModo("cadastro"); }}>
            Preencher manualmente
          </button>
        </p>
      </div>
    );
  }

  if (modo === "lendo") {
    return (
      <div className="flex flex-col gap-5" data-nova-lendo>
        <Cabine rotulo="Leitura automática" titulo="Lendo o despacho…"
          descricao="A leitura procura o processo, o tribunal, a vara, as partes e os prazos. Leva alguns segundos." />
        <div className="surface flex flex-col items-center gap-4 p-8 text-center" role="status" aria-live="polite">
          <svg viewBox="0 0 24 24" className="size-10 text-laranja-texto motion-safe:animate-spin" aria-hidden="true">
            <circle cx="12" cy="12" r="9" fill="none" stroke="currentColor" strokeWidth="2.5" strokeDasharray="14 43" strokeLinecap="round" />
          </svg>
          <p className="text-sm text-cinza">{anexo ? <>Lendo <span className="font-semibold text-titulo">{anexo.nome}</span>…</> : "Lendo o texto colado…"}</p>
          <button type="button" className={BOTAO_SECUNDARIO} onClick={cancelarLeitura} data-cancelar-leitura>Cancelar a leitura</button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-5" data-nova>
      <Cabine trilha={{ para: `${RAIZ}/pericias`, rotulo: "Perícias" }} rotulo="Cadastro" titulo="Nova perícia"
        descricao={<>Passo {passo + 1} de {PASSOS.length}: <span className="font-semibold text-white">{PASSOS[passo]}</span></>}>
        <div className="mt-5 max-w-xl">
          <Segmentos total={PASSOS.length} acesos={passo + 1} rotulo="Passos do cadastro" />
        </div>
      </Cabine>

      {leitura && (
        <div className={`rounded-cf-lg border p-4 text-sm ${leitura.recusa ? "border-alerta-tinta/40 bg-alerta-fundo text-alerta-tinta" : "border-borda bg-chip text-tinta"}`}
          role={leitura.recusa ? "alert" : "status"} data-leitura={leitura.recusa ? "recusa" : leitura.soNumero ? "so-numero" : "achou"}>
          {leitura.recusa ? (
            <p className="font-semibold">{leitura.recusa}</p>
          ) : leitura.soNumero ? (
            <p><span className="font-semibold">{doNumero.length ? `O número do processo preencheu ${emLista(doNumero)}.` : "O número do processo está no cadastro."}</span> Complete o resto nos passos.</p>
          ) : (
            <p><span className="font-semibold">A leitura achou {emLista(leitura.achou)}.</span> Confira cada campo antes de salvar: a leitura pode errar.</p>
          )}
          {leitura.recusa && leitura.achou.includes("o número do processo") && (
            <p className="mt-1">{doNumero.length ? `O número do processo, que estava no texto, já preencheu ${emLista(doNumero)}.` : "O número do processo, que estava no texto, já está no cadastro."}</p>
          )}
          {!leitura.soNumero && !leitura.recusa && leitura.faltou.length > 0 && (
            <p className="mt-1" data-leitura-faltou>Não achou {emLista(leitura.faltou)}: preencha nos passos.</p>
          )}
          {anexo && (
            <p className="mt-2 flex items-center gap-1.5" data-leitura-anexo>
              <IconeDocumento className="size-4 shrink-0" />
              <span>O arquivo <span className="font-semibold">{anexo.nome}</span> vai para os documentos da perícia quando você salvar.</span>
            </p>
          )}
          {leitura.podeTentarDeNovo && (
            <button type="button" className={`${BOTAO_SECUNDARIO} mt-3`} onClick={() => setModo("entrada")} data-tentar-de-novo>Tentar a leitura de novo</button>
          )}
        </div>
      )}

      <div className="surface p-5 sm:p-6">
        <nav aria-label="Passos do cadastro" className="-mx-1 overflow-x-auto px-1">
          <ol className="flex min-w-max gap-1.5">
            {PASSOS.map((nome, i) => (
              <li key={nome}>
                <button type="button" onClick={() => setPasso(i)} aria-current={passo === i ? "step" : undefined}
                  className={`min-h-9 rounded-full px-3.5 text-sm font-semibold whitespace-nowrap transition ${passo === i ? "bg-acento text-acento-tinta" : "text-cinza hover:bg-chip hover:text-titulo"}`}
                  data-passo={i}>
                  <span className="tabular-nums opacity-70">{i + 1}.</span> {nome}
                </button>
              </li>
            ))}
          </ol>
        </nav>

        <div className="mt-6 flex flex-col gap-4">
          {passo === 0 && (
            <>
              <Campo rotulo="Número do processo (CNJ)">
                <input className={`${CAMPO} tabular-nums`} value={numero} onChange={(e) => aoDigitarNumero(e.target.value)}
                  placeholder="0000000-00.0000.0.00.0000" inputMode="numeric" data-campo-numero />
                {numero && cnj && (
                  <span className={`text-xs font-semibold ${cnj.valido ? "text-aviso-tinta" : "text-erro-tinta"}`} data-cnj={cnj.valido ? "valido" : "invalido"}>
                    {cnj.valido ? "CNJ válido" : "O dígito verificador não confere: confira o número"}
                  </span>
                )}
              </Campo>
              <Campo rotulo="Apelido do processo" dica="Um nome curto para reconhecer o processo nas listas.">
                <input className={CAMPO} value={apelido} maxLength={40} placeholder="Ex.: Silva × Banco X" onChange={(e) => setApelido(e.target.value)} data-campo-apelido />
              </Campo>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <Campo rotulo="Tribunal">
                  <select className={CAMPO} value={tribunal} onChange={(e) => { setTribunal(e.target.value); setVara(""); }} data-campo-tribunal>
                    <option value="">Escolha</option>
                    {TRIBUNAIS.map((t) => <option key={t.sigla} value={t.sigla}>{t.sigla} — {t.nome}</option>)}
                  </select>
                </Campo>
                <Campo rotulo="Vara">
                  <select className={CAMPO} value={vara} onChange={(e) => setVara(e.target.value)} disabled={!tribunalObj} data-campo-vara>
                    <option value="">{tribunalObj ? "Escolha" : "Escolha o tribunal antes"}</option>
                    {tribunalObj?.varas.map((v) => <option key={v} value={v}>{v}</option>)}
                  </select>
                </Campo>
              </div>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <Campo rotulo="Fonte pagadora (Carnê-Leão)">
                  <input className={CAMPO} value={fontePagadora} maxLength={200} placeholder="Quem deposita os honorários" onChange={(e) => setFontePagadora(e.target.value)} />
                </Campo>
                <Campo rotulo="CPF ou CNPJ da fonte pagadora">
                  <input className={`${CAMPO} tabular-nums`} value={cpfCnpjFonte} inputMode="numeric" placeholder="00.000.000/0000-00"
                    onChange={(e) => setCpfCnpjFonte(mascaraCpfCnpj(e.target.value))} data-campo-cpf />
                  {cpfCnpjFonte && !cpfCnpjValido(cpfCnpjFonte) && <span className="text-xs font-semibold text-erro-tinta">Documento incompleto</span>}
                </Campo>
              </div>
            </>
          )}

          {passo === 1 && <PartesEditor valor={partes} aoMudar={setPartes} />}

          {passo === 2 && (
            <>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <Campo rotulo="Tipo de perícia">
                  <select className={CAMPO} value={tipo} onChange={(e) => setTipo(e.target.value)} data-campo-tipo>
                    <option value="">Escolha</option>
                    {TIPOS_PERICIA.map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                </Campo>
                <Campo rotulo="Prioridade">
                  <select className={CAMPO} value={prioridade} onChange={(e) => setPrioridade(e.target.value as Prioridade)}>
                    {(Object.keys(PRIORIDADE_LABEL) as Prioridade[]).map((p) => <option key={p} value={p}>{PRIORIDADE_LABEL[p]}</option>)}
                  </select>
                </Campo>
              </div>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                <Campo rotulo="Etapa atual">
                  <select className={CAMPO} value={etapa} onChange={(e) => trocarEtapa(Number(e.target.value) as EtapaNum)} data-campo-etapa>
                    {ETAPAS.map((e) => <option key={e} value={e}>{e}. {ETAPA_LABEL[e]}</option>)}
                  </select>
                </Campo>
                <Campo rotulo="Data da nomeação">
                  <input className={CAMPO} type="date" value={dataNomeacao} onChange={(e) => setDataNomeacao(e.target.value)} />
                </Campo>
                <Campo rotulo="Entrega do laudo">
                  <input className={CAMPO} type="date" value={dataEntrega} onChange={(e) => setDataEntrega(e.target.value)} />
                </Campo>
              </div>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                <Campo rotulo="Prazo para aceitar o encargo" dica="Vira um prazo na agenda e o prazo da tarefa de aceite.">
                  <input className={CAMPO} type="date" value={prazoAceitacao} onChange={(e) => mudarPrazoAceitacao(e.target.value)} data-campo-aceite />
                </Campo>
                <div className="sm:col-span-2">
                  <Campo rotulo="Próxima ação">
                    <input className={CAMPO} value={proximaAcao} maxLength={500} placeholder="Ex.: Cobrar documentos pendentes" onChange={(e) => setProximaAcao(e.target.value)} data-campo-proxima />
                  </Campo>
                </div>
              </div>
            </>
          )}

          {passo === 3 && <HonorariosEditor valor={honorarios} aoMudar={setHonorarios} />}

          {passo === 4 && (
            <div className="flex flex-col gap-4" data-passo-tarefas>
              <p className="text-sm text-cinza">
                Estas são as tarefas sugeridas para a etapa <span className="font-semibold text-titulo">{etapa}. {ETAPA_LABEL[etapa]}</span>.
                Tire as que não se aplicam, ajuste os prazos e inclua outras, se precisar.
              </p>
              {tarefas.length === 0 && (
                <p className="rounded-cf border border-dashed border-borda py-4 text-center text-sm text-cinza">
                  Nenhuma tarefa escolhida. Dá para incluir abaixo, ou depois, na ficha da perícia.
                </p>
              )}
              {tarefas.map((t) => {
                const m = t.modeloId ? buscarModelo(t.modeloId) : undefined;
                if (t.modeloId && !m) return null;
                const titulo = m?.titulo ?? t.titulo ?? "";
                return (
                  <div key={t.chave} className="rounded-cf border border-borda bg-fundo p-3.5" data-tarefa-inicial={t.modeloId ?? "propria"}>
                    <div className="flex flex-wrap items-start gap-3">
                      <div className="min-w-50 flex-1">
                        <div className="text-sm font-semibold text-titulo">{titulo}</div>
                        <div className="text-xs text-cinza">{m?.descricao ?? "Tarefa escrita por você"}</div>
                        {m && <div className="mt-1 text-xs text-cinza">{m.checklist.length} itens no checklist</div>}
                      </div>
                      <label className="flex w-44 flex-col gap-1 text-xs font-semibold text-tinta">Prazo
                        <input className={`${CAMPO} h-10`} type="date" value={t.prazo}
                          onChange={(e) => setTarefas((l) => l.map((x) => (x.chave === t.chave ? { ...x, prazo: e.target.value, prazoSugerido: false } : x)))} />
                      </label>
                      <button type="button" aria-label={`Tirar ${titulo}`} onClick={() => setTarefas((l) => l.filter((x) => x.chave !== t.chave))}
                        className="mt-5 grid size-10 place-items-center rounded-full text-cinza hover:bg-chip hover:text-erro-tinta">×</button>
                    </div>
                    {t.prazoSugerido && t.prazo && (
                      <p className="mt-2 text-xs font-semibold text-alerta-tinta">Prazo sugerido. Ajuste conforme o prazo definido na sua perícia.</p>
                    )}
                  </div>
                );
              })}

              <div className="border-t border-borda pt-4">
                <span className="text-sm font-semibold text-tinta">Escrever uma tarefa própria</span>
                <div className="mt-2 flex flex-wrap items-end gap-2">
                  <input className={`${CAMPO} min-w-50 flex-1`} value={novoTitulo} maxLength={300} aria-label="Título da tarefa"
                    placeholder="Ex.: Pedir cópia dos balancetes de 2023" onChange={(e) => setNovoTitulo(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); criarTarefaPropria(); } }} data-nova-tarefa />
                  <label className="flex w-44 flex-col gap-1 text-xs font-semibold text-tinta">Prazo (opcional)
                    <input className={CAMPO} type="date" value={novoPrazo} onChange={(e) => setNovoPrazo(e.target.value)} />
                  </label>
                  <button type="button" className={BOTAO_SECUNDARIO} onClick={criarTarefaPropria} disabled={!novoTitulo.trim()}><IconeMais /> Incluir</button>
                </div>
              </div>

              <div className="border-t border-borda pt-4">
                <span className="text-sm font-semibold text-tinta">Incluir outras tarefas prontas</span>
                <div className="mt-2 flex flex-wrap gap-2">
                  {TEMPLATES_TAREFA.filter((m) => !escolhidas.has(m.id)).map((m) => (
                    <button key={m.id} type="button" title={m.descricao} className={`${BOTAO_SECUNDARIO} min-h-9 text-xs`}
                      onClick={() => setTarefas((l) => [...l, { chave: m.id, modeloId: m.id, prazo: maisDias(hoje(), m.prazoDias), prazoSugerido: true }])}>
                      <IconeMais className="size-3.5" /> {m.etapa ? `${m.etapa}. ` : ""}{m.titulo}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {passo === 5 && (
            <Campo rotulo="Observações">
              <textarea className={`${CAMPO} h-40 py-3`} value={observacoes} maxLength={10000}
                placeholder="Notas internas, particularidades do caso…" onChange={(e) => setObservacoes(e.target.value)} />
            </Campo>
          )}
        </div>

        {erro && <p role="alert" className="mt-5 rounded-xl bg-erro-fundo p-3 text-sm text-erro-tinta" data-erro-cadastro>{erro}</p>}

        <div className="mt-6 flex justify-between gap-2 border-t border-borda pt-5">
          <button type="button" className={BOTAO_SECUNDARIO} disabled={passo === 0} onClick={() => setPasso((p) => Math.max(0, p - 1))}>← Voltar</button>
          {passo < PASSOS.length - 1 ? (
            <button type="button" className="pill-cta min-h-10 px-5 text-sm" onClick={() => setPasso((p) => p + 1)} data-proximo>Próximo →</button>
          ) : (
            <button type="button" className="pill-cta min-h-10 px-5 text-sm" onClick={salvar} data-salvar-pericia>Salvar perícia</button>
          )}
        </div>
      </div>
    </div>
  );
}
